import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Truck, CheckCircle, Phone } from 'lucide-react';
import api from '../utils/api';
import { useSettings } from '../context/SettingsContext';

const inputCls = "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-apex-blue-500 focus:ring-1 focus:ring-apex-blue-500";

const WHY = [
  ['💰','Top Cash Paid',       'We pay market value — cash in hand on the day'],
  ['⚡','Same Day Pickup',      'Available 7 days. Most Brisbane pickups within 2–4 hours'],
  ['🆓','100% Free Service',   'No towing fees, no admin fees — absolutely nothing to pay'],
  ['📋','We Handle Paperwork', 'Our team handles all transfer paperwork on the spot'],
  ['🔧','All Conditions',      'Running or not, crashed, unregistered — we take everything'],
  ['🌿','Eco Disposal',        'All vehicles recycled responsibly through licensed facilities'],
];

export default function CarRemovalPage() {
  const { settings, telHref } = useSettings();
  const [form, setForm] = useState({
    customer_name:'', customer_phone:'', customer_email:'',
    pickup_address:'', pickup_suburb:'',
    vehicle_make:'', vehicle_model:'', vehicle_year:'',
    vehicle_rego:'', vehicle_condition:'', notes:'',
  });
  const [done, setDone] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const canSubmit = form.customer_name && form.customer_phone && form.pickup_address;

  const submit = async () => {
    try {
      await api.post('/api/bookings', { ...form, service_type:'Free Car Removal', booking_type:'customer' });
      setDone(true);
    } catch {
      alert(`Failed to submit. Please call us directly on ${settings.company_phone}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-apex-blue-700 text-white px-4 py-5">
        <div className="max-w-xl mx-auto">
          <Link to="/" className="flex items-center gap-2 text-white/70 text-sm mb-4 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-apex-yellow-500 rounded-xl flex items-center justify-center">
              <Truck className="w-5 h-5 text-apex-blue-700" />
            </div>
            <div>
              <h1 className="font-black text-xl">Free Car Removal</h1>
              <p className="text-white/60 text-xs">All Brisbane suburbs — completely free of charge</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
          {WHY.map(([icon, title, desc]) => (
            <div key={title} className="bg-white rounded-2xl border border-gray-200 p-4 text-center hover:shadow-md transition-all">
              <div className="text-2xl mb-2">{icon}</div>
              <p className="font-black text-gray-900 text-sm mb-1">{title}</p>
              <p className="text-xs text-gray-500">{desc}</p>
            </div>
          ))}
        </div>

        {done ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-black text-apex-blue-700 mb-2">Request Received!</h2>
            <p className="text-gray-500 mb-6">
              We'll call <strong>{form.customer_phone}</strong> within 30 minutes to arrange pickup.
            </p>
            <div className="flex gap-3 justify-center">
              <a href={telHref()} className="flex items-center gap-2 bg-apex-yellow-500 text-apex-blue-700 font-black px-6 py-3 rounded-xl hover:bg-apex-yellow-400">
                <Phone className="w-4 h-4" /> {settings.company_phone}
              </a>
              <Link to="/" className="border-2 border-gray-200 text-gray-600 font-bold px-6 py-3 rounded-xl hover:bg-gray-50">Home</Link>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
            <h2 className="font-black text-gray-900 text-lg">Book Your Free Removal</h2>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-bold text-gray-500 uppercase">Your Name *</span>
                <input value={form.customer_name} onChange={set('customer_name')} className={`${inputCls} mt-1.5`} placeholder="John Smith" />
              </label>
              <label className="block">
                <span className="text-xs font-bold text-gray-500 uppercase">Phone Number *</span>
                <input type="tel" value={form.customer_phone} onChange={set('customer_phone')} className={`${inputCls} mt-1.5`} placeholder="07 0000 0000" />
              </label>
              <label className="block col-span-2">
                <span className="text-xs font-bold text-gray-500 uppercase">Email (optional)</span>
                <input type="email" value={form.customer_email} onChange={set('customer_email')} className={`${inputCls} mt-1.5`} />
              </label>
              <label className="block">
                <span className="text-xs font-bold text-gray-500 uppercase">Pickup Address *</span>
                <input value={form.pickup_address} onChange={set('pickup_address')} className={`${inputCls} mt-1.5`} placeholder="123 Main St" />
              </label>
              <label className="block">
                <span className="text-xs font-bold text-gray-500 uppercase">Suburb / Postcode</span>
                <input value={form.pickup_suburb} onChange={set('pickup_suburb')} className={`${inputCls} mt-1.5`} placeholder="Chermside QLD 4032" />
              </label>
            </div>

            <div className="border-t border-gray-100 pt-3">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Vehicle Details (optional but helpful)</p>
              <div className="grid grid-cols-3 gap-3">
                <label className="block"><span className="text-xs font-bold text-gray-400 uppercase">Make</span><input value={form.vehicle_make} onChange={set('vehicle_make')} className={`${inputCls} mt-1.5`} placeholder="Toyota" /></label>
                <label className="block"><span className="text-xs font-bold text-gray-400 uppercase">Model</span><input value={form.vehicle_model} onChange={set('vehicle_model')} className={`${inputCls} mt-1.5`} placeholder="Corolla" /></label>
                <label className="block"><span className="text-xs font-bold text-gray-400 uppercase">Year</span><input value={form.vehicle_year} onChange={set('vehicle_year')} className={`${inputCls} mt-1.5`} placeholder="2008" /></label>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <label className="block"><span className="text-xs font-bold text-gray-400 uppercase">Rego</span><input value={form.vehicle_rego} onChange={set('vehicle_rego')} className={`${inputCls} mt-1.5`} /></label>
                <label className="block"><span className="text-xs font-bold text-gray-400 uppercase">Condition</span>
                  <select value={form.vehicle_condition} onChange={set('vehicle_condition')} className={`${inputCls} mt-1.5 bg-white`}>
                    <option value="">Select</option>
                    <option value="running">Running</option>
                    <option value="not_running">Not Running</option>
                    <option value="accident_damaged">Accident Damaged</option>
                    <option value="scrap">Scrap / Parts only</option>
                  </select>
                </label>
              </div>
            </div>

            <label className="block">
              <span className="text-xs font-bold text-gray-500 uppercase">Additional Notes</span>
              <textarea value={form.notes} onChange={set('notes')} className={`${inputCls} mt-1.5 resize-none`} rows={2} placeholder="Access notes, best time to call, etc." />
            </label>

            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 text-center">
              <p className="font-black text-green-800">🎉 This service is 100% FREE</p>
              <p className="text-green-700 text-xs mt-0.5">No hidden fees. No surprises. We come to you anywhere in Brisbane.</p>
            </div>

            <button onClick={submit} disabled={!canSubmit}
              className={`w-full py-4 rounded-xl font-black flex items-center justify-center gap-2 text-base
                ${canSubmit ? 'bg-apex-yellow-500 text-apex-blue-700 hover:bg-apex-yellow-400' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
              <Truck className="w-5 h-5" /> Book Free Removal
            </button>

            <p className="text-center text-xs text-gray-400">
              Or call us directly:{' '}
              <a href={telHref()} className="font-bold text-apex-blue-700 underline">{settings.company_phone}</a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}